import { Component, OnInit, OnDestroy, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDividerModule } from '@angular/material/divider';
import { MatTabsModule } from '@angular/material/tabs';
import { MatListModule } from '@angular/material/list';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatBadgeModule } from '@angular/material/badge';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { Subject, takeUntil, debounceTime, distinctUntilChanged, forkJoin, catchError, of } from 'rxjs';

import { KeycloakClient, UserWithRoles } from '../../../core/models/client.model';
import { ClientService } from '../../../core/services/client.service';
import { RoleUserDTO, RoleStats } from '../../../core/models/role.model';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { UserService } from '../../../core/services/user.service';

@Component({
  selector: 'app-client-details',
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatChipsModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    MatDividerModule,
    MatTabsModule,
    MatListModule,
    MatSnackBarModule,
    MatExpansionModule,
    MatBadgeModule,
    MatSlideToggleModule,
    MatFormFieldModule,
    MatInputModule,
    MatPaginatorModule
  ],
  templateUrl: './client-details.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./client-details.component.scss']
})
export class ClientDetailsComponent implements OnInit, OnDestroy {
  client: KeycloakClient | null = null;
  clientId: string | null = null;
  loading = true;
  error: string | null = null;
  Object = Object;

  // Contrôle des sections expandables
  basicInfoExpanded = true;
  listeRolesExpanded: boolean = true;
  securityExpanded = true;
  urlsExpanded = true;
  advancedExpanded = false;

  // Gestion des rôles
  clientRoles: RoleUserDTO[] = [];
  rolesLoading = false;
  rolesError: string | null = null;
  totalRoles = 0;
  rolesFirst = 0;
  rolesMax = 50;
  rolesSearchTerm = '';
  private rolesSearchSubject = new Subject<string>();
  roleStats: RoleStats = {
    totalRoles: 0,
    compositeRoles: 0,
    simpleRoles: 0
  };

  private destroy$ = new Subject<void>();

  // Gestion des utilisateurs
  users: UserWithRoles[] = [];
  totalUsers = 0;
  usersPageSize = 10;
  usersCurrentPage = 0;
  usersLoading = false;
  usersError: string | null = null;
  selectedRoleFilter: string = '';// nom du rôle sélectionné pour le filtre

  // Liste des rôles pour le filtre dropdown
  roleOptions: { name: string; title?: string }[] = [];

  // Référence au paginator (à ajouter dans le template)
  @ViewChild('usersPaginator') usersPaginator!: MatPaginator;

  // Nouvel accordéon "Utilisateurs" (on ajoute une propriété)
  usersExpanded = true;

  usersSearchTerm = '';
  private usersSearchSubject = new Subject<string>();

  // ── Résolution code → sigle (cachée pour éviter les appels répétés) ──
  iesCodeToSigle = new Map<string, string>();
  etabCodeToSigle = new Map<string, string>();

  /** Codes actuellement en cours de résolution (évite les doublons d'appel). */
  private resolvingCodes = new Set<string>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private clientService: ClientService,
    private snackBar: MatSnackBar,
    private userService: UserService,
  ) { }

  ngOnInit(): void {
    this.route.params
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        this.clientId = params['id'];
        if (this.clientId) {
          this.loadClientDetails();
          this.loadClientRoles();
          this.loadUsers();
        } else {
          this.error = 'ID client non fourni';
          this.loading = false;
        }
      });

    // Configuration du debounce pour la recherche de rôles
    this.rolesSearchSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(searchTerm => {
        this.rolesFirst = 0;
        this.loadClientRoles();
      });

    this.usersSearchSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(term => {
        this.usersSearchTerm = term;
        this.usersCurrentPage = 0;
        if (this.usersPaginator) this.usersPaginator.firstPage();
        this.loadUsers();
      });

    // Charger les rôles pour le filtre dès que le client est chargé
    // this.route.params
    //   .pipe(takeUntil(this.destroy$))
    //   .subscribe(params => {
    //     this.clientId = params['id'];
    //     if (this.clientId) {
    //       this.loadClientDetails();
    //       this.loadClientRoles();
    //       this.loadUsers(); // <-- ajout
    //     }
    //   });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── handler (appelé depuis le template) ─────────────────────────
  onUsersSearchChange(term: string): void {
    this.usersSearchSubject.next(term);
  }

  /**
 * Charge les utilisateurs avec pagination et filtre éventuel
 */
  loadUsers(): void {
    if (!this.clientId) return;

    this.usersLoading = true;
    this.usersError = null;

    this.clientService.getUsersWithRolesForClient(
      this.clientId,
      this.usersCurrentPage * this.usersPageSize,
      this.usersPageSize,
      this.selectedRoleFilter || undefined,
      this.usersSearchTerm || undefined
    ).subscribe({
      next: (response) => {
        this.users = response.content || [];
        this.totalUsers = response.totalElements || 0;
        this.usersLoading = false;
        this.resolveRattachementCodes();
      },
      error: (error) => {
        console.error('Erreur lors du chargement des utilisateurs:', error);
        this.usersError = `Erreur: ${error.message}`;
        this.usersLoading = false;
        this.users = [];
        this.totalUsers = 0;
      }
    });
  }

  /**
   * Résout en lot tous les codes IES / Établissement présents dans la page courante.
   * Les codes déjà résolus (cache) sont ignorés.
   */
  private resolveRattachementCodes(): void {
    const iesCodes = new Set<string>();
    const etabCodes = new Set<string>();

    for (const user of this.users) {
      if (user.sigleIes) iesCodes.add(user.sigleIes);
      if (user.sigleEtablissements) {
        user.sigleEtablissements.forEach(c => c && etabCodes.add(c));
      }
    }

    this.resolveCodes(iesCodes, 'ies', this.iesCodeToSigle);
    this.resolveCodes(etabCodes, 'etab', this.etabCodeToSigle);
  }

  private resolveCodes(
    codes: Set<string>,
    kind: 'ies' | 'etab',
    target: Map<string, string>
  ): void {
    const pending = [...codes].filter(
      c => !target.has(c) && !this.resolvingCodes.has(`${kind}:${c}`)
    );
    if (pending.length === 0) return;

    pending.forEach(c => this.resolvingCodes.add(`${kind}:${c}`));

    forkJoin(
      pending.map(code =>
        this.userService.getSigleByCode(code).pipe(
          catchError(() => of(code))   // fallback : on affiche le code
        )
      )
    ).subscribe({
      next: (sigles) => {
        pending.forEach((code, i) => {
          target.set(code, sigles[i] || code);
          this.resolvingCodes.delete(`${kind}:${code}`);
        });
      },
      error: () => {
        // Ne devrait pas arriver (catchError en amont), sécurité
        pending.forEach(code => {
          target.set(code, code);
          this.resolvingCodes.delete(`${kind}:${code}`);
        });
      }
    });
  }

  // ── Helpers pour le template ──────────────────────────────────────────

  getIesSigleFor(code: string | null | undefined): string {
    if (!code) return '';
    return this.iesCodeToSigle.get(code) ?? '';
  }

  isIesResolving(code: string | null | undefined): boolean {
    if (!code) return false;
    return !this.iesCodeToSigle.has(code);
  }

  getEtabSiglesFor(codes: string[] | null | undefined): string[] {
    if (!codes) return [];
    return codes
      .map(c => this.etabCodeToSigle.get(c) ?? '')
      .filter(s => s.length > 0);
  }

  isEtabResolving(codes: string[] | null | undefined): boolean {
    if (!codes) return false;
    return codes.some(c => !this.etabCodeToSigle.has(c));
  }

  /**
   * Gère le changement de page du paginateur
   */
  onUsersPageChange(event: PageEvent): void {
    this.usersCurrentPage = event.pageIndex;
    this.usersPageSize = event.pageSize;
    this.loadUsers();
  }

  /**
   * Applique le filtre par rôle (dropdown)
   */
  onRoleFilterChange(roleName: string): void {
    this.selectedRoleFilter = roleName;
    this.usersCurrentPage = 0;
    if (this.usersPaginator) {
      this.usersPaginator.firstPage();
    }
    this.loadUsers();
  }

  /**
   * Initialise les options du filtre à partir des rôles du client
   */
  initRoleOptions(): void {
    // On peut utiliser les rôles déjà chargés (clientRoles)
    if (this.clientRoles && this.clientRoles.length > 0) {
      this.roleOptions = this.clientRoles.map(r => ({
        name: r.name,
        title: r.title || r.name
      }));
      // Ajouter une option "Tous"
      this.roleOptions.unshift({ name: '', title: 'Tous les rôles' });
    }
  }

  /**
   * Charge les détails du client
   */
  loadClientDetails(): void {
    if (!this.clientId) return;

    this.loading = true;
    this.error = null;

    this.clientService.getClient(this.clientId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (client) => {
          this.client = client;
          this.loading = false;
        },
        error: (error) => {
          console.error('Erreur lors du chargement du client:', error);
          this.error = `Erreur lors du chargement: ${error.message}`;
          this.loading = false;
        }
      });
  }

  /**
   * Charge les rôles du client
   */
  loadClientRoles(): void {
    if (!this.clientId) return;

    this.rolesLoading = true;
    this.rolesError = null;

    this.clientService.getClientRoles(
      this.clientId,
      this.rolesFirst,
      this.rolesMax,
      this.rolesSearchTerm
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.clientRoles = response.content || [];
          this.totalRoles = response.totalElements || 0;
          this.calculateRoleStats();
          this.initRoleOptions();
          this.rolesLoading = false;
        },
        error: (error) => {
          console.error('Erreur lors du chargement des rôles:', error);
          this.rolesError = `Erreur lors du chargement des rôles: ${error.message}`;
          this.rolesLoading = false;
        }
      });
  }

  /**
   * Calcule les statistiques des rôles
   */
  calculateRoleStats(): void {
    if (!this.clientRoles || !Array.isArray(this.clientRoles)) {
      this.roleStats = { totalRoles: 0, compositeRoles: 0, simpleRoles: 0 };
      return;
    }
    this.roleStats = {
      totalRoles: this.totalRoles,
      compositeRoles: this.clientRoles.filter(r => r.composite).length,
      simpleRoles: this.clientRoles.filter(r => !r.composite).length
    };
  }

  /**
   * Gère la recherche de rôles avec debounce
   */
  onRolesSearchChange(searchTerm: string): void {
    this.rolesSearchTerm = searchTerm;
    this.rolesSearchSubject.next(searchTerm);
  }

  /**
   * Efface la recherche de rôles
   */
  clearRolesSearch(): void {
    this.rolesSearchTerm = '';
    this.rolesSearchSubject.next('');
  }

  /**
   * Charge plus de rôles (pagination)
   */
  loadMoreRoles(): void {
    if (this.clientRoles.length < this.totalRoles) {
      this.rolesFirst += this.rolesMax;
      this.loadClientRoles();
    }
  }

  /**
   * Retourne à la liste des clients
   */
  goBack(): void {
    this.router.navigate(['/dashboard/clients']);
  }

  /**
   * Navigue vers la page d'édition
   */
  editClient(): void {
    if (this.client?.id) {
      this.router.navigate(['/dashboard/clients/edit', this.client.id]);
    }
  }

  /**
   * Copie une valeur dans le presse-papier
   */
  copyToClipboard(value: string, label: string): void {
    navigator.clipboard.writeText(value).then(() => {
      this.showSuccessMessage(`${label} copié dans le presse-papier`);
    }).catch(() => {
      this.showErrorMessage(`Impossible de copier ${label.toLowerCase()}`);
    });
  }

  /**
   * Régénère le secret du client
   */
  regenerateSecret(): void {
    if (!this.client || this.client.publicClient) {
      this.showErrorMessage('Les clients publics n\'ont pas de secret');
      return;
    }

    const confirmed = confirm(
      `Êtes-vous sûr de vouloir régénérer le secret du client "${this.client.name || this.client.clientId}" ?\n\n` +
      `L'ancien secret ne fonctionnera plus.`
    );

    if (confirmed && this.client.id) {
      this.clientService.regenerateClientSecret(this.client.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            navigator.clipboard.writeText(response.secret).then(() => {
              this.showSuccessMessage(
                `Nouveau secret généré et copié dans le presse-papier`
              );
              // Optionnel: recharger les détails du client
              this.loadClientDetails();
            }).catch(() => {
              this.showSuccessMessage(
                `Nouveau secret généré: ${response.secret.substring(0, 10)}...`
              );
            });
          },
          error: (error) => {
            console.error('Erreur lors de la régénération du secret:', error);
            this.showErrorMessage(`Erreur lors de la régénération: ${error.message}`);
          }
        });
    }
  }

  /**
   * Supprime le client
   */
  deleteClient(): void {
    if (!this.client) return;

    const confirmed = confirm(
      `Êtes-vous sûr de vouloir supprimer le client "${this.client.name || this.client.clientId}" ?\n\n` +
      `Cette action est irréversible et supprimera définitivement ce client.`
    );

    if (confirmed && this.client.id) {
      this.clientService.deleteClient(this.client.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.showSuccessMessage(`Client "${this.client!.name || this.client!.clientId}" supprimé avec succès`);
            this.router.navigate(['/dashboard/clients']);
          },
          error: (error) => {
            console.error('Erreur lors de la suppression:', error);
            this.showErrorMessage(`Erreur lors de la suppression: ${error.message}`);
          }
        });
    }
  }

  /**
   * Génère les initiales pour l'avatar
   */
  getInitials(): string {
    if (!this.client) return '??';
    const displayName = this.client.name || this.client.clientId;
    return displayName.substring(0, 2).toUpperCase();
  }

  /**
   * Génère une couleur pour l'avatar
   */
  getAvatarColor(): string {
    if (!this.client) return '#45B56F';

    const colors = [
      '#45B56F', '#f093fb', '#45B56F', '#43e97b', '#fa709a',
      '#ff6b6b', '#4ecdc4', '#45b7d1', '#96ceb4', '#feca57'
    ];
    let hash = 0;
    for (let i = 0; i < this.client.clientId.length; i++) {
      hash = this.client.clientId.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  }

  /**
   * Obtient le type de client
   */
  getClientType(): string {
    if (!this.client) return 'Inconnu';
    if (this.client.bearerOnly) return 'Bearer Only';
    if (this.client.publicClient) return 'Public';
    return 'Confidentiel';
  }

  /**
   * Obtient la couleur du type de client
   */
  getClientTypeColor(): string {
    if (!this.client) return '#9E9E9E';
    if (this.client.bearerOnly) return '#9c27b0';
    if (this.client.publicClient) return '#45B56F';
    return '#4caf50';
  }

  /**
   * Obtient le statut formaté
   */
  getStatusInfo(): { text: string, color: string, icon: string } {
    if (!this.client) return { text: 'Inconnu', color: '#9E9E9E', icon: 'help' };

    if (this.client.enabled) {
      return { text: 'Actif', color: '#4caf50', icon: 'check_circle' };
    } else {
      return { text: 'Inactif', color: '#f44336', icon: 'cancel' };
    }
  }

  /**
   * Vérifie si une URL est valide
   */
  isValidUrl(url: string): boolean {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Ouvre une URL dans un nouvel onglet
   */
  openUrl(url: string): void {
    if (this.isValidUrl(url)) {
      window.open(url, '_blank');
    }
  }

  /**
   * Obtient les flows activés
   */
  getEnabledFlows(): Array<{ name: string, enabled: boolean, description: string }> {
    if (!this.client) return [];

    return [
      {
        name: 'Standard Flow',
        enabled: this.client.standardFlowEnabled,
        description: 'Authorization Code Flow (recommandé)'
      },
      {
        name: 'Implicit Flow',
        enabled: this.client.implicitFlowEnabled,
        description: 'Déprécié, utiliser Standard Flow'
      },
      {
        name: 'Direct Access Grants',
        enabled: this.client.directAccessGrantsEnabled,
        description: 'Resource Owner Password Flow'
      },
      {
        name: 'Service Accounts',
        enabled: this.client.serviceAccountsEnabled,
        description: 'Client Credentials Flow'
      }
    ];
  }

  /**
   * Obtient la couleur du badge de rôle
   */
  getRoleBadgeColor(role: RoleUserDTO): string {
    if (role.composite) return '#8b5cf6';
    return '#10b981';
  }

  /**
   * Obtient l'icône du rôle
   */
  getRoleIcon(role: RoleUserDTO): string {
    if (role.composite) return 'folder';
    return 'label';
  }

  /**
   * Affiche un message de succès
   */
  private showSuccessMessage(message: string): void {
    this.snackBar.open(message, 'Fermer', {
      duration: 4000,
      panelClass: ['success-snackbar'],
      horizontalPosition: 'end',
      verticalPosition: 'top'
    });
  }

  /**
   * Affiche un message d'erreur
   */
  private showErrorMessage(message: string): void {
    this.snackBar.open(message, 'Fermer', {
      duration: 6000,
      panelClass: ['error-snackbar'],
      horizontalPosition: 'end',
      verticalPosition: 'top'
    });
  }

  /**
   * Retourne le nombre de flows activés
   */
  getEnabledFlowsCount(): number {
    return this.getEnabledFlows().filter(f => f.enabled).length;
  }

  /**
 * Retourne le libellé affiché pour le type du client
 */
  getTypeLabel(): string {
    if (!this.client) return 'Non défini';
    const map: Record<string, string> = {
      'external-app': 'Externe',
      'internal-app': 'Interne',
      'neutral-app': 'Neutre'
    };
    return map[this.client.type || ''] || 'Non défini';
  }

  /**
   * Retourne la couleur associée au type du client
   */
  getTypeColor(): string {
    if (!this.client) return '#9E9E9E';
    const map: Record<string, string> = {
      'external-app': '#4caf50',  // vert
      'internal-app': '#2196f3',  // bleu
      'neutral-app': '#ff9800'    // orange
    };
    return map[this.client.type || ''] || '#9E9E9E';
  }
}